import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getPostDetails } from '../../api/postsApi';
import SEOHelmet from '../../components/SEO/SEOHelmet';

import style from './BlogArticleDetails.module.css';

const DEFAULT_LOGO =
  'https://dating-date-photos.s3.us-east-2.amazonaws.com/Logo+IKA.png';
const BASE_URL = 'https://artemidastudio.com';

/** UA/RU → латинка (спрощений трансліт) */
const translitMap = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'h',
  ґ: 'g',
  д: 'd',
  е: 'e',
  є: 'ie',
  ж: 'zh',
  з: 'z',
  и: 'y',
  і: 'i',
  ї: 'i',
  й: 'i',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'kh',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'shch',
  ь: '',
  ю: 'iu',
  я: 'ia',
  ё: 'e',
  э: 'e',
  ы: 'y',
};
const transliterate = (s = '') =>
  s.replace(/[А-Яа-яЁёЄєІіЇїҐґЭэЫы]/g, ch => {
    const lower = ch.toLowerCase();
    const t = translitMap[lower] ?? '';
    return ch === lower ? t : t.charAt(0).toUpperCase() + t.slice(1);
  });

/** Безпечний slugify з транслітом і фолбеком */
const safeSlugify = (s = '', fallback = '') => {
  const base = transliterate(s)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return base || fallback || 'post';
};

/** Побудова канонічного шляху:
 *  EN:    /blog/post/:slug/:id
 *  Інші:  /:lang/blog/post/:slug/:id
 */
const buildArticlePath = (lang, slug, id) =>
  lang === 'en'
    ? `/blog/post/${slug || 'post'}/${id}`
    : `/${lang}/blog/post/${slug || 'post'}/${id}`;

/** Абсолютний URL (для зображень/посилань) */
const absoluteUrl = u => {
  if (!u) return undefined;
  if (/^https?:\/\//i.test(u)) return u;
  return `${BASE_URL}${u.startsWith('/') ? '' : '/'}${u}`;
};

/** Створюємо короткий опис, якщо metaDescription порожній */
const makeExcerpt = (text = '', maxLen = 160) => {
  const t = String(text).replace(/\s+/g, ' ').trim();
  if (!t) return '';
  if (t.length <= maxLen) return t;
  const cut = t.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > 80 ? cut.slice(0, lastSpace) : cut) + '…';
};

const BlogArticleDetails = () => {
  const { lang = 'en', id, slug } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);

  // Завантажуємо деталі статті
  useEffect(() => {
    let cancelled = false;
    getPostDetails(id, { language: lang }).then(data => {
      if (!cancelled) setItem(data || null);
    });
    return () => {
      cancelled = true;
    };
  }, [lang, id]);

  // Після завантаження — вирівнюємо URL (slug) до канонічного
  useEffect(() => {
    if (!item) return;

    const backendSlug = item.slug && String(item.slug).trim();
    const expectedSlug =
      backendSlug || safeSlugify(item.title || '', `post-${id?.slice?.(0, 6)}`);

    if (expectedSlug && slug !== expectedSlug) {
      const canonicalPath = buildArticlePath(lang, expectedSlug, id);
      navigate(canonicalPath, { replace: true });
    }
  }, [item, slug, lang, id, navigate]);

  // Форматування дат для UI
  const dateFmt = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(lang, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return new Intl.DateTimeFormat('en', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
  }, [lang]);

  if (!item) return null;

  const {
    title,
    imageUrl,
    metaDescription,
    mainText,
    metaKeywords,
    createdAt,
    updatedAt,
    slug: backendSlug, // якщо бекенд дає
    authorName, // якщо є автор у БД
  } = item;

  const cover = absoluteUrl(String(imageUrl || '').trim()) || DEFAULT_LOGO;

  // description: або з БД, або згенерований з контенту
  const description =
    (metaDescription && String(metaDescription).trim()) ||
    makeExcerpt(mainText || '');

  // keywords: масив або кома-рядок → уніфікуємо в рядок
  const keywordsArr = Array.isArray(metaKeywords)
    ? metaKeywords
    : String(metaKeywords || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
  const keywordsStr = keywordsArr.join(', ');

  const createdISO = createdAt || undefined;
  const updatedISO = updatedAt || createdAt || undefined;

  const createdPretty = createdISO
    ? dateFmt.format(new Date(createdISO))
    : null;
  const updatedPretty = updatedISO
    ? dateFmt.format(new Date(updatedISO))
    : null;

  // Канонічний шлях (на основі актуального slug)
  const canonicalSlug =
    (backendSlug && String(backendSlug).trim()) ||
    safeSlugify(title || '', `post-${id?.slice?.(0, 6)}`);
  const canonicalPath = buildArticlePath(lang, canonicalSlug, id);

  return (
    <div className={style.page}>
      {/* УВАГА: весь SEO/OG/Twitter/JSON-LD тепер в одному місці — в SEOHelmet */}
      <SEOHelmet
        pageKey="article" // може бути будь-який ключ, ми все одно передаємо override
        override={{
          title: title || 'Article — Artemida Studio',
          description,
          keywords: keywordsStr,
          canonical: canonicalPath,
          ogImage: cover,
          article: {
            datePublished: createdISO,
            dateModified: updatedISO,
            authorName: authorName || 'Artemida Studio',
          },
        }}
      />

      <div className={style.header}>
        <button className={style.backBtn} onClick={() => navigate(-1)}>
          ← Back
        </button>

        <h1 className={style.title}>{title || 'Untitled'}</h1>

        {description && <p className={style.subtitle}>{description}</p>}

        <div className={style.metaRow}>
          {createdPretty && (
            <span className={style.metaItem}>
              <time dateTime={createdISO}>{createdPretty}</time>
            </span>
          )}
          {updatedPretty && updatedPretty !== createdPretty && (
            <span className={style.metaItem}>
              Updated: <time dateTime={updatedISO}>{updatedPretty}</time>
            </span>
          )}
        </div>

        {keywordsArr.length > 0 && (
          <ul className={style.tags}>
            {keywordsArr.map(k => (
              <li key={k} className={style.tag}>
                {k}
              </li>
            ))}
          </ul>
        )}
      </div>

      {cover && (
        <div className={style.coverWrap}>
          <div className={style.gradient}>
            <img
              className={style.coverImg}
              src={cover}
              alt={title || 'Cover image'}
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      )}

      <article className={style.article}>
        {String(mainText || '')
          .split(/\n{2,}/)
          .map(s => s.trim())
          .filter(Boolean)
          .map((p, i) => (
            <p className={style.p} key={i}>
              {p}
            </p>
          ))}
      </article>
    </div>
  );
};

export default BlogArticleDetails;
